import {API_URL} from "../config/envConfig";
import { CampaignParticipation, Campaign, Info } from "../data/FrontendTypes";

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const idToken = sessionStorage.getItem('idToken');
    const accessToken = sessionStorage.getItem('accessToken');

    if (!idToken || !accessToken) {
        console.error('Auth tokens not found in sessionStorage:', { 
            hasIdToken: !!idToken, 
            hasAccessToken: !!accessToken 
        });
        throw new Error('Authentication tokens not found. Please try logging in again.');
    }

    if (!idToken.startsWith('ey') || !accessToken.startsWith('ey')) {
        console.error('Invalid token format detected');
        throw new Error('Invalid authentication tokens. Please try logging in again.');
    }

    const headers = {
        'Authorization': `Bearer ${idToken}`,
        'X-BTB-Access-Token': accessToken,
        'Content-Type': 'application/json',
        ...options.headers,
    };

    try {
        const response = await fetch(url, { ...options, headers });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('API request failed:', {
                url,
                status: response.status,
                statusText: response.statusText,
                error: errorText
            });
            throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        return response;
    } catch (error) {
        console.error('Network error during API request:', {
            url,
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        throw error;
    }
}

/**
 * Berechnet die Distanz zwischen zwei Orten mit einem bestimmten Verkehrsmittel
 * @param origin Startpunkt
 * @param destination Ziel
 * @param mode Verkehrsmittel
 * @returns Objekt mit der Distanz in Metern
 */

export type CalculateDistanceProps = {
    origin: string;
    destination: string;
    mode: 'driving' | 'transit' | 'bicycling';
}

export async function calculateDistance(props: CalculateDistanceProps): Promise<string> {

    const response = await fetchWithAuth(`${API_URL}/calculateDistance`, {
        method: 'POST',
        body: JSON.stringify(props) 
    });
    const result: string = await response.text(); //z.B. "12km"
    return result;
}

/**
 * Wird direkt nach dem Login aufgerufen. Erhält nöige Infos für die App
 * @returns admin - ob der Benutzer Admin ist
 * @returns campaignParticipation - Infos zur aktuellen Kampagne und den Trips des Benutzers
 */

export async function getInfo(): Promise<Info> {
    const response = await fetchWithAuth(`${API_URL}/info`, { method: 'GET' });
    const result: Info = await response.json();
    return result;
}

/**
 * Speichert die geänderten Trips.
 * Holt sich die KampagnenId aus dem Param und die BenutzerId im Backend aus den Token.
 * @param campaignParticipation - Infos zur aktuellen Kampagne und den Trips des Benutzers
 */


export async function storeCampaignParticipation(campaignParticipation: CampaignParticipation): Promise<void> {
    const options = {
        method: 'POST',
        body: JSON.stringify(campaignParticipation)
    }
    await fetchWithAuth(`${API_URL}/campaignParticipation`, options);
    return;
}

/**
 * Holt die Infos der aktuellen Kampagnen-Teilnahme (campaignParticipation)
 * Wird nach Speicherungen aufgerufen, um den Stand zu aktualisieren
 * @param campaignId - Die ID der Kampagne
 * @returns Infos der aktuellen Kampagnen-Teilname (campaignParticipation)
 */
export async function getTrips(): Promise<CampaignParticipation> {
    const response = await fetchWithAuth(`${API_URL}/campaignParticipation`, { method: 'GET' });
    const result: CampaignParticipation = await response.json();
    return result;
}

/**
 * ADMIN ONLY
 * Erstellt eine neue Kampagne mit default Werten, die im Backend festgelegt sind.
 */
export async function createCampaign(): Promise<void> {
    await fetchWithAuth(`${API_URL}/admin/campaigns/new`, { method: 'POST' });
    return;
}

/**
 * ADMIN ONLY
 * Holt alle Kampagnen
 * @returns Array mit allen Kampagnen
 */
export async function getAllCampaigns(): Promise<Campaign[]> {
    const response = await fetchWithAuth(`${API_URL}/admin/campaigns`, { method: 'GET' });
    const result: Campaign[] = await response.json();
    
    // Parse date strings back to Date objects for frontend use
    const campaignsWithParsedDates = result.map(campaign => ({
        ...campaign,
        endDate: typeof campaign.endDate === 'string' 
            ? new Date(campaign.endDate) 
            : campaign.endDate
    }));
    
    return campaignsWithParsedDates;
}

/**
 * ADMIN ONLY
 * Updated eine Kampagne
 * @param campaign - Die Kampagne die geupdated werden soll
 */
export async function storeCampaign(campaign: Campaign): Promise<void> {
    // Convert the campaign object to have the date in the correct format for the backend
    const campaignForBackend = {
        ...campaign,
        endDate: campaign.endDate instanceof Date 
            ? (() => {
                const year = campaign.endDate.getFullYear();
                const month = String(campaign.endDate.getMonth() + 1).padStart(2, '0');
                const day = String(campaign.endDate.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              })() // Convert to yyyy-MM-dd format using local date components
            : campaign.endDate
    };
    
    await fetchWithAuth(`${API_URL}/admin/${campaign.id}`, { 
        method: 'PUT', 
        body: JSON.stringify(campaignForBackend) 
    });
    return;
}

/**
 * ADMIN ONLY
 * Löscht eine Kampagne
 * @param campaignId - Die ID der Kampagne
 */
export async function deleteCampaign(campaignId: string): Promise<void> {
    await fetchWithAuth(`${API_URL}/admin/${campaignId}`, { method: 'DELETE' });
    return;
}

/**
 * ADMIN ONLY
 * Exportiert die Daten einer Kampagne
 * @param campaignId - Die ID der Kampagne
 */
export async function exportCampaign(campaignId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/admin/${campaignId}/export`, { method: 'GET' });
    
    // Get the response as blob directly
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign-${campaignId}-export.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return;
}